import type {FC} from 'react';
import {useState} from 'react';
import {
    Back,
    Container,
    ContentContainer,
    PageTitle,
    StyledTextField,
    palette,
} from '@src/core/styles';

import {useNavigate} from 'react-router-dom';
import {ReactComponent as Arrow} from '@src/core/icons/arrow.svg';
import {ReactComponent as Check} from '@src/core/icons/check.svg';
import {Controller, useForm} from 'react-hook-form';
import type {
    SupportErrorResponse,
    SupportErrors,
    SupportRequestParams,
} from '@src/core/types';
import {joiResolver} from '@hookform/resolvers/joi';
import {supportSchema} from '@src/core/schemas';
import {DescriptionText} from '../price-page/price-page.styles';
import {TitleWrapper} from './offers-form.styles';
import {FieldGroupWrapper} from '../login-page/login-page.styles';
import {Button} from '@src/components';
import {FormContainer} from '../route-management-page/components/form-component/form-components.styles';
import {Support} from '@src/core/services';
import {useMutation} from '@tanstack/react-query';
import {AxiosError} from 'axios';
import {enqueueSnackbar} from 'notistack';
import {Captcha} from "@src/components/captcha";


export const OffersForm: FC = () => {
    const navigate = useNavigate();
    const [errorsRequest, setErrorsRequest] = useState<SupportErrors>();

    const [captchaState, setCaptchaState] = useState<boolean>(false);

    const captchaValidation = (value: any) => {
        setCaptchaState(value);

        return value;
    }

    const {
        handleSubmit,
        control,
        formState: {errors, isValid},
    } = useForm<SupportRequestParams>({
        mode: 'onChange',
        resolver: joiResolver(supportSchema),
        defaultValues: {
            name: '',
            email: '',
            message: '',
        },
    });

    const fullValidation = isValid && captchaState;


    const {mutate} = useMutation(Support.offer, {
        onSuccess: (response) => {
            enqueueSnackbar('Сообщение отправлено', {
                variant: 'reportComplete',
                className: 'success',
                preventDuplicate: true,
            });
        },
        onError: (error: AxiosError<SupportErrorResponse>) => {
            setErrorsRequest(error.response?.data.errors);
        },
    });

    const backHandler = () => {
        navigate(-1);
    };

    const onSubmit = (data: SupportRequestParams) => {
        mutate(data);
    };

    const changeHandler = () => {
        setErrorsRequest(undefined);
    };

    return (
        <Container>
            <ContentContainer>
                <FormContainer>
                    <Back onClick={backHandler}>
                        <Arrow/>
                    </Back>
                    <TitleWrapper>
                        <PageTitle>Предложение по улучшению</PageTitle>
                        <DescriptionText>
                            Опишите идею по улучшение эффективности сервиса и заполните данные
                            для обратной&nbsp;связи
                        </DescriptionText>
                    </TitleWrapper>
                    <form onSubmit={handleSubmit(onSubmit)}>
                        <FieldGroupWrapper>
                            <Controller
                                control={control}
                                name="name"
                                render={({field: {value, onBlur, onChange}}) => (
                                    <StyledTextField
                                        onBlur={onBlur}
                                        variant="outlined"
                                        label="Имя"
                                        autoComplete="on"
                                        InputLabelProps={{
                                            sx: {
                                                '&.Mui-focused': {
                                                    color: `${palette.colors.green_base}`,
                                                },
                                                '&.Mui-error': {
                                                    color: `${palette.colors.error}`,
                                                },
                                            },
                                        }}
                                        value={value}
                                        error={!!errors?.name?.message || !!errorsRequest?.name}
                                        helperText={errors?.name?.message || errorsRequest?.name}
                                        onChange={(e) => {
                                            changeHandler();
                                            onChange(e);
                                        }}
                                    />
                                )}
                            />
                            <Controller
                                control={control}
                                name="email"
                                render={({field: {value, onBlur, onChange}}) => (
                                    <StyledTextField
                                        onBlur={onBlur}
                                        variant="outlined"
                                        label="Электронная почта"
                                        placeholder="Электронная почта"
                                        autoComplete="on"
                                        InputLabelProps={{
                                            sx: {
                                                '&.Mui-focused': {
                                                    color: `${palette.colors.green_base}`,
                                                },
                                                '&.Mui-error': {color: `${palette.colors.error}`},
                                            },
                                        }}
                                        value={value}
                                        error={!!errors?.email?.message || !!errorsRequest?.email}
                                        helperText={errors?.email?.message || errorsRequest?.email}
                                        onChange={(e) => {
                                            changeHandler();
                                            onChange(e);
                                        }}
                                    />
                                )}
                            />
                            <Controller
                                control={control}
                                name="message"
                                render={({field: {value, onBlur, onChange}}) => (
                                    <StyledTextField
                                        onBlur={onBlur}
                                        variant="outlined"
                                        label="Опишите суть вашего предложения"
                                        multiline
                                        inputProps={{maxLength: 1000}}
                                        autoComplete="on"
                                        rows={5}
                                        InputLabelProps={{
                                            sx: {
                                                '&.Mui-focused': {
                                                    color: `${palette.colors.green_base}`,
                                                },
                                                '&.Mui-error': {color: `${palette.colors.error}`},
                                            },
                                        }}
                                        value={value}
                                        error={
                                            !!errors?.message?.message || !!errorsRequest?.message
                                        }
                                        helperText={
                                            errors?.message?.message || errorsRequest?.message
                                        }
                                        onChange={(e) => {
                                            changeHandler();
                                            onChange(e);
                                        }}
                                    />
                                )}
                            />
                        </FieldGroupWrapper>
                        <Captcha CaptchaState={captchaValidation}/>

                        <Button
                            text="Отправить"
                            type="submit"
                            variant="contained"
                            icon={<Check/>}
                            borderradius="L"
                            disabled={!fullValidation}
                        />
                    </form>
                </FormContainer>
            </ContentContainer>
        </Container>
    );
};
